import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSeo, fileUrl } from "../lib/seo";
import { Check, ShieldCheck, FileCheck2, HandCoins, GraduationCap } from "lucide-react";

export function TierCards({ billing, onPick, currentKey, busy }) {
  const [tiers, setTiers] = useState([]);
  useEffect(() => { api.get("/ngos/tiers").then((r) => setTiers(r.data.items)); }, []);
  return (
    <div className="grid md:grid-cols-3 gap-6">
      {tiers.map((t) => (
        <div key={t.key} data-testid={`tier-${t.key}`} className={`rounded-3xl p-7 flex flex-col ${t.priority ? "bg-[#4A0E17] text-[#FAF6F0]" : "bg-[#FFFDF9] sacred-border text-[#1F1412]"}`}>
          <div className={`overline ${t.priority ? "text-[#D4AF37]" : ""}`}>{t.featured ? "Featured" : "Starter"}</div>
          <h3 className={`font-serif text-3xl mt-2 ${t.priority ? "text-[#F3E5AB]" : "text-[#4A0E17]"}`}>{t.name}</h3>
          <div className="mt-4 font-mono text-4xl">${billing === "monthly" ? t.monthly_usd : t.yearly_usd}<span className="text-sm opacity-70">/{billing === "monthly" ? "mo" : "yr"}</span></div>
          <ul className="mt-6 space-y-2 text-sm flex-1">
            {(t.perks || []).map((p) => <li key={p} className="flex gap-2"><Check className="w-4 h-4 text-[#E65100] shrink-0 mt-0.5" /> {p}</li>)}
          </ul>
          {onPick && (
            <button data-testid={`tier-pick-${t.key}`} disabled={busy} onClick={() => onPick(t)} className={`mt-7 ${t.priority ? "btn-saffron" : "btn-ghost-gold"} justify-center`}>
              {currentKey === t.key ? "Renew" : "Choose"} {t.name}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function BillingToggle({ billing, setBilling }) {
  return (
    <div className="inline-flex bg-[#FFFDF9] border border-[#D4AF37]/40 rounded-full p-1">
      {["yearly", "monthly"].map((b) => (
        <button key={b} data-testid={`billing-${b}`} onClick={() => setBilling(b)} className={`px-5 h-9 rounded-full text-sm capitalize transition-sacred ${billing === b ? "bg-[#4A0E17] text-[#F3E5AB]" : "text-[#4A0E17]"}`}>{b}</button>
      ))}
    </div>
  );
}

const STEPS = [
  { icon: FileCheck2, t: "Register", d: "Upload your registration certificate (Darpan, 12A, 80G, FCRA or equivalent) with your registration number and PAN." },
  { icon: ShieldCheck, t: "Get verified", d: "Our team checks your documents, usually within 3 working days." },
  { icon: HandCoins, t: "Subscribe & request", d: "Pick a tier and submit project requests. Approved projects are funded from the Seva Fund." },
];

export default function NgoProgram() {
  const [billing, setBilling] = useState("yearly");
  const [partners, setPartners] = useState([]);
  useSeo("NGO Partner Program");
  useEffect(() => { api.get("/ngos/partners").then((r) => setPartners(r.data.items)); }, []);

  return (
    <div data-testid="ngo-program-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="overline">॥ सेवा परमो धर्मः ॥</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#4A0E17] mt-2 leading-tight max-w-3xl">NGO Partner Program</h1>
      <p className="text-[#63534E] mt-5 text-base md:text-lg max-w-2xl">A share of every platform fee goes into the <b>Seva Fund</b>. Registered NGOs who subscribe can ask for project funding, and every grant is published on our <Link to="/transparency" className="text-[#E65100]">Transparency page</Link>.</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/ngo/apply" data-testid="ngo-apply-cta" className="btn-saffron">Register your NGO</Link>
        <Link to="/scholarships" data-testid="ngo-scholarship-link" className="btn-ghost-gold"><GraduationCap className="w-4 h-4" /> Student scholarships</Link>
      </div>

      <div className="mt-16 grid md:grid-cols-3 gap-6">
        {STEPS.map(({ icon: I, t, d }, i) => (
          <div key={t} className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
            <div className="flex items-center gap-3"><span className="font-mono text-[#8C6D3B]">0{i + 1}</span><I className="w-5 h-5 text-[#E65100]" /></div>
            <h3 className="font-serif text-xl text-[#4A0E17] mt-3">{t}</h3>
            <p className="text-sm text-[#63534E] mt-2">{d}</p>
          </div>
        ))}
      </div>

      <div className="mt-16 flex flex-wrap items-end justify-between gap-4">
        <h2 className="font-serif text-3xl text-[#4A0E17]">Subscription tiers</h2>
        <BillingToggle billing={billing} setBilling={setBilling} />
      </div>
      <div className="mt-6"><TierCards billing={billing} /></div>

      {partners.length > 0 && (
        <section className="mt-16">
          <h2 className="font-serif text-3xl text-[#4A0E17]">Our NGO partners</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {partners.map((n) => (
              <div key={n.id} data-testid={`partner-${n.id}`} className={`bg-[#FFFDF9] rounded-2xl p-5 ${n.featured ? "border-2 border-[#D4AF37]" : "sacred-border"}`}>
                <div className="flex items-center gap-3">
                  {n.logo_url ? <img src={fileUrl(n.logo_url)} alt="" className="w-12 h-12 rounded-full object-cover" /> : <span className="w-12 h-12 rounded-full bg-[#4A0E17] text-[#D4AF37] flex items-center justify-center font-serif text-xl">{n.org_name[0]}</span>}
                  <div><div className="font-serif text-lg text-[#4A0E17]">{n.org_name}</div><div className="text-xs text-[#8C6D3B]">{n.tier_name}</div></div>
                </div>
                <p className="text-sm text-[#63534E] mt-3 line-clamp-3">{n.mission}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
